import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  CheckCircle,
  ChatCircleDots,
  Compass,
  FileText,
  Target,
  TrendUp,
} from "@phosphor-icons/react";
import WhistleIcon from "@/components/WhistleIcon";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import SeoPageShell from "@/components/SeoPageShell";
import SeoContextFooter from "@/components/SeoContextFooter";
import SeoHeroSpecimen from "@/components/SeoHeroSpecimen";

const analysisLayers = [
  {
    icon: ChatCircleDots,
    label: "Evidence",
    title: "Anchor the analysis in what was actually said.",
    copy: "Surface the customer and rep moments that changed the commercial direction of the call, so every conclusion can be traced back to the conversation.",
  },
  {
    icon: Brain,
    label: "Interpretation",
    title: "Explain what those moments mean.",
    copy: "Turn raw statements into a view of buying intent, decision process, objections, risk, urgency, commitments, and stakeholder dynamics.",
  },
  {
    icon: WhistleIcon,
    label: "Coaching",
    title: "Show what could have been handled differently.",
    copy: "Connect coaching to a specific moment and explain why another approach could have changed the conversation, rather than serving generic best practices.",
  },
  {
    icon: Target,
    label: "Next move",
    title: "Convert the analysis into action.",
    copy: "Carry the important signals into the next customer conversation: what to validate, what to ask, what to avoid, and what outcome to pursue.",
  },
  {
    icon: Compass,
    label: "Relationship context",
    title: "Remember what changed across calls.",
    copy: "Track objections, stakeholders, promises, risk, and buying intent longitudinally so every new call starts from the real state of the relationship.",
  },
];

const buyingChecklist = [
  "Can you see the evidence behind each conclusion?",
  "Does the tool separate transcript and summary from actual interpretation?",
  "Is coaching tied to moments from the call instead of generic scorecards?",
  "Does the analysis produce a concrete next move for the rep?",
  "Can it remember objections, commitments, stakeholders, and changes across conversations?",
  "Does the next-call preparation use the customer's real history rather than a generic playbook?",
];

function trackCta(section, ctaId) {
  trackPublicEvent("cta_click", {
    source: "sales_call_analysis_software",
    section,
    cta_id: ctaId,
  });
}

export default function SalesCallAnalysisSoftwarePage() {
  useEffect(() => {
    const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    scrollToTop();
    const frame = window.requestAnimationFrame(scrollToTop);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <SeoPageShell source="sales_call_analysis_software">
      <main>
        <section className="seo-marketing-hero product-feature-hero relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-24 sm:pt-24">
<div className="seo-marketing-hero__layout relative mx-auto max-w-7xl">
      
            <h1 className="seo-marketing-hero__title product-feature-hero__title landing-hero-title max-w-5xl">
              Sales call analysis software should tell you
              <span className="sales-call-review-gradient product-feature-hero__title-accent landing-hero-spectrum block">what changed—and what to do next.</span>
            </h1>
      
            <p className="seo-marketing-hero__subcopy product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy">
              A transcript, summary, talk ratio, or scorecard can describe a call. Useful sales call analysis software should go further: connect evidence to interpretation, coaching, the next move, and the relationship context that carries into future conversations. That is the workflow Callsect is built around.
            </p>
      
            <div className="seo-marketing-hero__actions product-feature-hero__actions">
              <Link
                to="/login?mode=register"
                onClick={() => trackCta("hero", "sign_up_free")}
                className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => trackCta("hero", "see_demo")}
                className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
            <SeoHeroSpecimen variant="analysis" />
          </div>
        </section>
      
        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">The baseline is not the outcome</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Recording the call is useful. Understanding it is the job.
              </h2>
              <p className="marketing-lead-copy mt-6">
                Most call tools can capture what happened. The real question is whether the software helps a rep understand why it mattered and use that understanding in the next customer conversation.
              </p>
            </div>
      
            <div className="sales-call-analysis-compare mt-12">
              <div className="sales-call-analysis-compare__column">
                <div className="sales-call-analysis-compare__eyebrow">CAPTURE LAYER</div>
                <h3>Useful inputs</h3>
                <div className="sales-call-analysis-compare__items">
                  <div><FileText size={19} weight="duotone" /><span>Transcript</span></div>
                  <div><FileText size={19} weight="duotone" /><span>Summary</span></div>
                  <div><TrendUp size={19} weight="duotone" /><span>Talk ratio and metrics</span></div>
                  <div><CheckCircle size={19} weight="duotone" /><span>Scorecards</span></div>
                </div>
                <p>These make a call easier to retrieve and inspect. They do not automatically explain the commercial meaning of the conversation.</p>
              </div>
      
              <div className="sales-call-analysis-compare__arrow" aria-hidden="true">
                <ArrowRight size={24} weight="bold" />
              </div>
      
              <div className="sales-call-analysis-compare__column is-analysis">
                <div className="sales-call-analysis-compare__eyebrow">ANALYSIS LAYER</div>
                <h3>Decision-useful output</h3>
                <div className="sales-call-analysis-compare__items">
                  <div><ChatCircleDots size={19} weight="duotone" /><span>Evidence</span></div>
                  <div><Brain size={19} weight="duotone" /><span>Interpretation</span></div>
                  <div><WhistleIcon size={19} weight="duotone" /><span>Coaching</span></div>
                  <div><Target size={19} weight="duotone" /><span>Next move</span></div>
                  <div><Compass size={19} weight="duotone" /><span>Relationship context</span></div>
                </div>
                <p>This is where the software becomes useful after the call: what changed, why it matters, what you could have done differently, and what should happen next.</p>
              </div>
            </div>
      
            <div className="mt-6 text-sm leading-6 text-slate-500">
              Looking at the category boundary instead?{" "}
              <Link
                to="/conversation-intelligence-vs-call-summarization"
                onClick={() => trackCta("internal_link", "conversation_intelligence_vs_call_summarization")}
                className="seo-inline-editorial-link"
              >
                Conversation intelligence vs call summarization
              </Link>
              .
            </div>
          </div>
        </section>
      
        <section id="what-good-analysis-does" className="scroll-mt-24 px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">What good analysis should do</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Five layers turn a call into something you can act on.
              </h2>
              <p className="marketing-lead-copy mt-6">
                The layers matter because each one answers a different question. Skip one and the analysis becomes harder to trust, harder to apply, or easier to forget.
              </p>
            </div>
      
            <div className="sales-call-analysis-layers mt-12">
              {analysisLayers.map(({ icon: Icon, label, title, copy }, index) => (
                <article key={label} className="sales-call-analysis-layer">
                  <div className="sales-call-analysis-layer__rail">
                    <span>0{index + 1}</span>
                    <div className="sales-call-analysis-layer__icon"><Icon size={20} weight="duotone" /></div>
                  </div>
                  <div>
                    <span className="sales-call-analysis-layer__label">{label}</span>
                    <h3>{title}</h3>
                    <p>{copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      
        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-16">
            <div className="lg:sticky lg:top-24">
              <span className="sales-call-review-kicker">A concrete example</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                The same sentence can be a quote, a signal, and a next move.
              </h2>
              <p className="marketing-lead-copy mt-6">
                The value is not in extracting the sentence. It is in connecting that sentence to the state of the deal and the next conversation.
              </p>
            </div>
      
            <div className="sales-call-review-example">
              <div className="sales-call-review-example__header">
                <span>Example analysis</span>
                <span>Evaluation call</span>
              </div>
      
              <div className="sales-call-review-example__quote sales-call-analysis-example__quote">
                <span className="sales-call-analysis-example__speaker-group">
                  <span className="sales-call-review-speaker">CLIENT</span>
                  <ChatCircleDots size={22} weight="duotone" aria-hidden="true" />
                </span>
                <blockquote>
                  “Budget isn't really the issue. I need our operations lead to believe changing the process is worth the disruption.”
                </blockquote>
              </div>
      
              <div className="sales-call-review-example__analysis">
                <div>
                  <span>EVIDENCE</span>
                  <p>The customer explicitly separates budget from internal operational buy-in.</p>
                </div>
                <div>
                  <span>INTERPRETATION</span>
                  <p>The commercial risk is not price. The unresolved decision hinges on perceived implementation disruption and an uninvolved stakeholder.</p>
                </div>
                <div>
                  <span className="seo-coaching-label"><WhistleIcon size={14} weight="duotone" />COACHING</span>
                  <p>Do not keep defending ROI or discounting. Bring the operations concern into the conversation and help the customer make the change feel operationally credible.</p>
                </div>
              </div>
      
              <div className="sales-call-review-example__next">
                <span>NEXT MOVE</span>
                <p>Get the operations lead into the next call and prepare around transition risk, implementation effort, and what would make the change feel safe enough to approve.</p>
              </div>
            </div>
          </div>
        </section>
      
        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <div>
              <span className="sales-call-review-kicker">Choosing a tool</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Compare sales call analysis software on what happens after the summary.
              </h2>
              <p className="marketing-lead-copy mt-6">
                Feature lists make many products look similar. A better evaluation is to upload or inspect the same conversation and see how far each product carries the reasoning.
              </p>
            </div>
      
            <div className="grid gap-4">
              {buyingChecklist.map((item) => (
                <div key={item} className="sales-call-review-check">
                  <CheckCircle size={21} weight="fill" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
          <SeoContextFooter
            label="Related comparison"
            to="/gong-alternative-for-account-managers"
            onClick={() => trackCta("internal_link", "gong_alternative_for_account_managers")}
            className="seo-context-footer--section mx-auto mt-8 max-w-7xl"
          >
            Gong alternative for account managers
          </SeoContextFooter>
        </section>
      
        <section className="px-5 pb-10 sm:px-8 sm:pb-14">
          <div className="sales-call-analysis-review-link mx-auto max-w-5xl">
            <div>
              <span className="sales-call-review-kicker">Prefer the review framework?</span>
              <h2>Learn how to review a sales call step by step.</h2>
              <p>If your question is less about choosing software and more about what a useful review should contain, start with the sales call review guide.</p>
            </div>
            <Link
              to="/sales-call-review"
              onClick={() => trackCta("internal_link", "sales_call_review")}
              className="marketing-consistency-secondary-link product-feature-final__secondary"
            >
              Read the sales call review guide
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </section>
      
        <section className="px-5 pb-20 pt-6 sm:px-8 sm:pb-28 sm:pt-10">
          <div className="product-feature-final sales-call-review-cta mx-auto max-w-5xl">
            <span className="sales-call-review-kicker">See Callsect in context</span>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              Follow a sales call from evidence to the next move.
            </h2>
            <p className="marketing-lead-copy mx-auto mt-6 max-w-2xl">
              Explore the real Callsect interface using a synthetic sales conversation: Dissection, Coaching, CRM Note, Relationship, and Prepare Next Call.
            </p>
            <div>
              <Link
                to="/login?mode=register"
                onClick={() => trackCta("final", "sign_up_free")}
                className="product-feature-final__primary"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => trackCta("final", "see_demo")}
                className="product-feature-final__secondary"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SeoPageShell>
  );
}
